// Functions for working with our "views" database table.
// The views table is used to store user-generated "documents",
// ie, any structured data that needs to be persisted. As I
// write this, reports, workspaces and Vega panels are stored
// in views.
//
// We want this to be a generic mechanism that we can use
// for fast frontend development. It's likely that we'll add
// many more types of things here, as the product evolves.
//
// The views database table has the following fields:
//   user_id: id of user who created the view
//   type: a string, that represents the type of object
//     being stored.
//   name: an arbitrary string. Currently used as report
//     and Vega panel names. For workspaces it's either
//     'workspace' or 'default' (potentially postfixed
//     with an ID string).
//   description: arbitrary string.
//   spec: JSON blob. This is where all the data is stored.
//     Fully controlled by the frontend. The backend treats
//     it as opaque.

import {omit} from 'lodash';
import {getType} from 'typesafe-actions';

import {startPerfTimer} from '../../util/datadog/profiler';
import {startImmerPerformanceMeasure} from '../../util/datadog/workspace/immerPerformance';
import {startImmutablePerformanceMeasure} from '../../util/datadog/workspace/immutablePerformance';
import {updateIn} from '../../util/utility';
import * as RunSelectorRunsActions from '../runSelectorRuns/actions';
import * as Actions from './actions';
import * as ActionsInternal from './actionsInternal';
import * as CustomRunNamesActions from './customRunNames/actions';
import * as FilterActions from './filter/actions';
import * as GroupSelectionsActions from './groupSelections/actions';
import {handleUpdateViewSpec} from './handleUpdateViewSpec';
import {immerViewsReducer} from './immerReducer';
import * as Normalize from './normalize';
// It would probably be nice to write separate reducers for sub-dirs
import * as PanelActions from './panel/actions';
import * as PanelBankConfigActions from './panelBankConfig/actions';
import * as PanelBankConfigActionsInternal from './panelBankConfig/actionsInternal';
import * as PanelBankConfigUtils from './panelBankConfig/utils';
import * as PanelBankSectionConfigActions from './panelBankSectionConfig/actions';
import {
  type ActionType,
  deletePartsImmutable,
  immutableRemoveHistoryForObject,
  replacePartImmutable,
  type ViewReducerState,
} from './reducerSupport';
import * as RunSetActions from './runSet/actions';
import {
  fixupRunsetBaselineRuns,
  removeMissingPinnedRunIds,
  updateRunSet,
} from './runSet/utils';
import * as SectionActions from './section/actions';
import * as SemanticLegendActions from './semanticLegend/actions';
import * as TempSelectionsActions from './tempSelections/actions';
import {
  applyAndMakeInverseActionImmutable,
  applyUndoableActionImmutable,
} from './undoableActions';
import {copyObjectImmutable} from './util';
import * as WorkspaceSettingsActions from './workspaceSettings/actions';

export const initialState = {
  lists: {},
  views: {},
  parts: {
    'project-view': {},
    'group-view': {},
    'sweep-view': {},
    'run-view': {},
    runs: {},
    'runs/draft': {},
    'collection-overview': {},
    'collection-overview/draft': {},
    section: {},
    'markdown-block': {},
    runSet: {},
    sort: {},
    filters: {},
    panels: {},
    panel: {},
    panelSettings: {},
    'group-selections': {},
    'run-colors': {},
    'run-names': {},
    'temp-selections': {},
    'panel-bank-config': {},
    'panel-bank-section-config': {},
    'discussion-thread': {},
    'discussion-comment': {},
    'workspace-settings': {},
    'semantic-legend': {},
  },
  loading: false,
  undoActions: [],
  redoActions: [],
};

function viewsReducer(
  state: ViewReducerState = initialState,
  action: ActionType
) {
  // When Redux initializes a reducer, it appends a random string to the end
  // of the action type. This is to make it difficult to handle inside of
  // application code, by design. We are not handling it, but we want a stable
  // identifier for analytics purposes.
  const analyticsActionType = action.type.match(/@@redux\/INIT/)
    ? '@@redux/INIT'
    : action.type;
  const {endPerfTimer} = startPerfTimer(
    `view reducer, action: ${analyticsActionType}`
  );
  const endImmutableMeasure =
    startImmutablePerformanceMeasure(analyticsActionType);
  let fellThroughToImmer = false;

  try {
    // actions ejected from immer due to poor performance
    switch (action.type) {
      case getType(ActionsInternal.undoableUpdateViewSpec):
      case getType(CustomRunNamesActions.setCustomRunName):
      case getType(GroupSelectionsActions.toggleSelection):
      case getType(GroupSelectionsActions.toggleExpandedRowAddress):
      case getType(GroupSelectionsActions.setGrouping):
      case getType(PanelActions.setConfig):
      case getType(PanelActions.updateConfig):
      case getType(PanelBankConfigActions.addPanelsBySpec):
      case getType(PanelBankConfigActions.addPanelsBySpecUndo):
      case getType(PanelBankConfigActions.clearAllPanels):
      case getType(PanelBankConfigActions.clearAllPanelsUndo):
      case getType(PanelBankConfigActions.moveSection):
      case getType(PanelBankConfigActions.movePanel):
      case getType(PanelBankConfigActions.undoMovePanel):
      case getType(PanelBankConfigActions.openOrCloseAllSections):
      case getType(PanelBankConfigActions.updateAllLinePlotSectionSettings):
      case getType(PanelBankSectionConfigActions.addPanelByConfig):
      case getType(PanelBankSectionConfigActions.addPanelByRef):
      case getType(PanelBankSectionConfigActions.addPanelsByConfig):
      case getType(PanelBankSectionConfigActions.applyCopiedPanelConfig):
      case getType(PanelBankSectionConfigActions.toggleIsOpen):
      case getType(PanelBankSectionConfigActions.undoApplyCopiedPanelConfig):
      case getType(PanelBankSectionConfigActions.updateFlowConfig):
      case getType(PanelBankSectionConfigActions.updateLinePlotSectionSettings):
      case getType(PanelBankSectionConfigActions.updateSectionMediaSettings):
      case getType(PanelBankSectionConfigActions.unsetSectionMediaSetting):
      case getType(PanelBankSectionConfigActions.unsetSectionMediaSettings):
      case getType(PanelBankSectionConfigActions.updateName):
      case getType(RunSetActions.update):
      case getType(RunSetActions.setPinnedRun):
      case getType(RunSetActions.setBulkPinnedRuns):
      case getType(RunSetActions.setBaselineRun):
      case getType(SectionActions.addNewRunSet):
      case getType(WorkspaceSettingsActions.updateLinePlotWorkspaceSettings):
      case getType(WorkspaceSettingsActions.updateWorkspaceMediaSettings):
      case getType(WorkspaceSettingsActions.unsetWorkspaceMediaSetting):
      case getType(WorkspaceSettingsActions.unsetWorkspaceMediaSettings):
      case getType(WorkspaceSettingsActions.updateAutoOrganizePrefix):
      case getType(WorkspaceSettingsActions.updateAutoOrganizePrefixUndo):
      case getType(WorkspaceSettingsActions.updateShouldAutoGeneratePanels):
      case getType(WorkspaceSettingsActions.updateWorkspaceLayoutSettings):
      case getType(
        WorkspaceSettingsActions.updateSemanticLegendRunColorOptions
      ):
      case getType(
        WorkspaceSettingsActions.updateSemanticLegendRunColorOptionsAndClearCustomRunColors
      ):
      case getType(
        WorkspaceSettingsActions.updateSemanticLegendRunColorOptionsAndClearCustomRunColorsUndo
      ):
      case getType(WorkspaceSettingsActions.updateColorPaletteOption):
      case getType(WorkspaceSettingsActions.updateSemanticLegendPalette):
      case getType(TempSelectionsActions.selectAllVisible):
      case getType(TempSelectionsActions.selectNone):
      case getType(TempSelectionsActions.selectToggle):
      case getType(TempSelectionsActions.selectSome):
      case getType(TempSelectionsActions.selectAll):
      case getType(FilterActions.set):
      case getType(PanelBankConfigActions.setCurrentPanelSearch):
      case getType(PanelBankConfigActions.updateSettings): {
        return applyUndoableActionImmutable(state, action);
      }

      case getType(Actions.undo): {
        try {
          const undoItem = state.undoActions[state.undoActions.length - 1];
          if (!undoItem) {
            break;
          }
          const [newState, redoAction] = applyAndMakeInverseActionImmutable(
            state,
            undoItem.action
          );
          // must preserve ID when moving between undo and redo history!
          const redoItem = {id: undoItem.id, action: redoAction};
          newState.undoActions = state.undoActions.slice(0, -1);
          newState.redoActions = state.redoActions.concat(redoItem);
          return newState;
        } catch (err) {
          // the immutable handler doesn't know how to undo this action but
          // the immer one might; break here so it flows into immer block
          break;
        }
      }

      case getType(Actions.redo): {
        const redoItem = state.redoActions[state.redoActions.length - 1];
        if (!redoItem) {
          break;
        }
        try {
          const [newState, undoAction] = applyAndMakeInverseActionImmutable(
            state,
            redoItem.action
          );
          // must preserve ID when moving between undo and redo history!
          const undoItem = {id: redoItem.id, action: undoAction};
          newState.undoActions = state.undoActions.concat(undoItem);
          newState.redoActions = state.redoActions.slice(0, -1);
          return newState;
        } catch (err) {
          // the immutable handler doesn't know how to redo this action but
          // the immer one might; break here so it flows into immer block
          break;
        }
      }

      case getType(Actions.rename): {
        const {ref, name} = action.payload;
        return updateIn(state, `views.${ref.id}.displayName`, () => name);
      }

      case getType(ActionsInternal.deleteHistoryForObject): {
        const {ref} = action.payload;
        return immutableRemoveHistoryForObject(state, ref);
      }

      case getType(ActionsInternal.loadFinished): {
        const {
          cid: viewCid,
          result: view,
          autoSave,
          panelCommentsEnabled,
        } = action.payload;

        const stateView = state.views[viewCid];
        if (stateView != null && stateView.type !== view.type) {
          throw new Error('View type change');
        }

        const {parts: newParts, ref: partRef} = Normalize.addObjImmutable(
          state.parts,
          view.type,
          viewCid,
          view.spec
        );

        return {
          ...state,
          parts: newParts,
          loading: false,
          views: {
            ...state.views,
            [viewCid]: {
              ...omit(action.payload.result, 'spec'),
              loading: false,
              saving: false,
              modified: false,
              undoIdAtLastSave: undefined,
              starLoading: false,
              autoSave,
              partRef,
              panelCommentsEnabled,
            },
          },
        };
      }

      case getType(ActionsInternal.updateViewSpec): {
        return handleUpdateViewSpec(
          state,
          action.payload.id,
          action.payload.spec
        );
      }

      case getType(Actions.addObject): {
        const {wholeAndType, ref} = action.payload;
        const {ref: addedRef, parts: newParts} = Normalize.addObjImmutable(
          state.parts,
          wholeAndType.type,
          ref.viewID,
          wholeAndType.whole
        );
        if (ref.type !== addedRef.type) {
          throw new Error('invalid action');
        }
        return replacePartImmutable({...state, parts: newParts}, ref, addedRef);
      }

      case getType(PanelBankConfigActionsInternal.diffAndInitPanels): {
        const {ref, expectedPanels, workspaceSettingsRef} = action.payload;

        const {shouldAutoGeneratePanels, sortAlphabetically} =
          state.parts[workspaceSettingsRef.type][workspaceSettingsRef.id] ?? {};
        if (shouldAutoGeneratePanels === 'pending') {
          // no-op; user must explicitly select auto or manual mode first
          return state;
        }

        return PanelBankConfigUtils.immutableDiffAndInitPanels(
          state,
          ref,
          expectedPanels,
          sortAlphabetically,
          shouldAutoGeneratePanels
        );
      }
      case getType(PanelBankConfigActionsInternal.replacePanelBankConfig): {
        const {config, ref} = action.payload;
        const {parts: newParts, ref: addedRef} = Normalize.addObjImmutable(
          state.parts,
          'panel-bank-config',
          ref.viewID,
          config
        );

        let newState = replacePartImmutable(
          {...state, parts: newParts},
          ref,
          addedRef
        );

        // Apply panel placement and config overrides after replacing
        // (the cached panel bank might not have overrides applied)
        // Note: use `ref` not `addedRef` because replacePartImmutable keeps the original ref
        newState = PanelBankConfigUtils.applyPanelPlacementOverrides(
          newState,
          ref
        );
        newState = PanelBankConfigUtils.applyPanelConfigOverrides(
          newState,
          ref
        );

        return newState;
      }
      case getType(SemanticLegendActions.setSemanticLegendSettings): {
        const {semanticLegendSettings, ref} = action.payload;
        return {
          ...state,
          parts: {
            ...state.parts,
            [ref.type]: {
              ...state.parts[ref.type],
              [ref.id]: semanticLegendSettings,
            },
          },
        };
      }
      // This action is separate from setCurrentPanelSearch because it fires on blur instead of debounce
      case getType(PanelBankConfigActions.updatePanelSearchHistory): {
        const {ref, searchHistory} = action.payload;

        return updateIn(state, `parts.${ref.type}.${ref.id}.settings`, v => ({
          ...v,
          searchHistory,
        }));
      }

      case getType(Actions.copyObject): {
        const {fromRef, ref} = action.payload;
        return copyObjectImmutable(state, fromRef, ref);
      }

      case getType(ActionsInternal.deleteObject): {
        const {ref} = action.payload;
        return deletePartsImmutable(state, ref);
      }

      case getType(ActionsInternal.saveFinished): {
        const {cid, result, undoIdAtSaveStart} = action.payload;
        return updateIn(state, `views.${cid}`, viewsCid => ({
          ...viewsCid,

          // we have to update these values from the server
          updatedAt: result.updatedAt,
          updatedBy: result.updatedBy,
          previewUrl: result.previewUrl,
          coverUrl: result.coverUrl,
          id: result.id,

          saving: false,
          modified: false,
          // The saga snapshots the undo-stack tail at save-start and threads
          // it through saveFinished, so any actions dispatched while this
          // save was in flight remain correctly marked as unsaved.
          undoIdAtLastSave: undoIdAtSaveStart,
          user: result.user,
        }));
      }

      case getType(RunSelectorRunsActions.processQueryResults): {
        const {
          data: {pinnedRuns},
          queryId,
          requestedPinnedRunIds,
        } = action.payload;
        const ref = {type: 'runSet', id: queryId, viewID: ''} as const;

        return updateRunSet(state, ref, (runSet, groupSelections) =>
          fixupRunsetBaselineRuns(
            removeMissingPinnedRunIds(
              runSet,
              pinnedRuns || [],
              groupSelections,
              requestedPinnedRunIds
            )
          )
        );
      }
    }

    fellThroughToImmer = true;

    // fall back on immer
    const endImmerMeasure = startImmerPerformanceMeasure(analyticsActionType);
    try {
      return immerViewsReducer(state, action);
    } finally {
      endImmerMeasure();
    }
  } finally {
    if (!fellThroughToImmer) {
      endImmutableMeasure();
    }
    endPerfTimer();
  }
}

export default viewsReducer;
